import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { atualizarJson, enviarJson, pedirJson } from "./api";

export const esquemaConsulta = z.object({
  situacao: z.string(),
  mensagem: z.string(),
  papel: z.string().nullable().optional(),
});
export const esquemaContato = z.object({
  id: z.string(),
  nome: z.string(),
  cargo: z.string().nullable(),
  email: z.string(),
  telefone: z.string().nullable(),
  situacao: z.string(),
  enviado_em: z.string().nullable(),
  expira_em: z.string().nullable(),
  aceito_em: z.string().nullable(),
  gerado_em: z.string().nullable().optional(),
  envio: z.string().nullable().optional(),
  aviso_envio: z.string().nullable().optional(),
  aviso_enviado_em: z.string().nullable().optional(),
});
export const esquemaContatos = z.object({
  contatos: z.array(esquemaContato),
  historico: z.array(
    z.object({
      acao: z.string(),
      ocorrido_em: z.string(),
      ator: z.string().nullable(),
      detalhe: z.record(z.string(), z.unknown()),
    }),
  ),
});
export const esquemaExclusao = z.object({
  excluida: z.boolean(),
  sinalizacao_externa: z.string(),
  mensagem: z.string(),
  referencias: z.array(z.string()),
});
export type ContatoDeAcesso = z.infer<typeof esquemaContato>;
export interface DadosDoContato {
  nome: string;
  cargo: string | null;
  email: string;
  telefone: string | null;
  vincular_existente: boolean;
}

export function useConsultaEmail(email: string, cnpj: string) {
  const [consulta, setConsulta] = useState({ email: "", cnpj: "" });
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setConsulta({
          email: email.trim().toLowerCase(),
          cnpj: cnpj.replace(/\D/g, ""),
        }),
      350,
    );
    return () => clearTimeout(timer);
  }, [email, cnpj]);
  const atual =
    email.trim().toLowerCase() === consulta.email &&
    cnpj.replace(/\D/g, "") === consulta.cnpj;
  const valida =
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(consulta.email) &&
    consulta.cnpj.length === 14;
  const resultado = useQuery({
    queryKey: ["consulta-email-implantacao", consulta],
    queryFn: ({ signal }) =>
      pedirJson(
        `/implantacoes/contatos/consultar-email?${new URLSearchParams(consulta)}`,
        esquemaConsulta,
        { signal },
      ),
    enabled: valida && atual,
    staleTime: 0,
    retry: false,
  });
  return {
    ...resultado,
    data: atual ? resultado.data : undefined,
    isError: atual && resultado.isError,
    consultando:
      /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) &&
      cnpj.replace(/\D/g, "").length === 14 &&
      (!atual || resultado.isFetching),
  };
}

export function useContatos(id: string, habilitado: boolean) {
  return useQuery({
    queryKey: ["contatos-implantacao", id],
    queryFn: () => pedirJson(`/implantacoes/${id}/contatos`, esquemaContatos),
    enabled: habilitado,
    refetchInterval: (query) =>
      query.state.data?.contatos.some((c) => c.envio === "pendente" || c.aviso_envio === "pendente")
        ? 2000
        : false,
  });
}

export function useSalvarContato(id: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({
      contatoId,
      dados,
    }: {
      contatoId?: string;
      dados: DadosDoContato;
    }) =>
      contatoId
        ? atualizarJson(
            `/implantacoes/${id}/contatos/${contatoId}`,
            esquemaContato,
            dados,
          )
        : enviarJson(`/implantacoes/${id}/contatos`, esquemaContato, dados),
    onSuccess: () =>
      cache.invalidateQueries({ queryKey: ["contatos-implantacao", id] }),
  });
}

export function useReenviarContato(id: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (contatoId: string) =>
      enviarJson(
        `/implantacoes/${id}/contatos/${contatoId}/reenviar`,
        esquemaContato,
        {},
      ),
    onSuccess: () =>
      cache.invalidateQueries({ queryKey: ["contatos-implantacao", id] }),
  });
}

export function useExcluirImplantacao(id: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () =>
      enviarJson(`/implantacoes/${id}/excluir`, esquemaExclusao, {
        confirmacao: id,
      }),
    onSuccess: () =>
      cache.invalidateQueries({ queryKey: ["implantacoes", "listagem"] }),
  });
}
